import { useCallback, useState } from "react";

import { PlusIcon, SendIcon } from "@lucide/icons";

import { RichText } from "simplydo/react/richtext";
import { styled } from "simplydo/react/styled";
import { Button } from "simplydo/react/ui/button";
import { Checkbox } from "simplydo/react/ui/checkbox";
import { H5, Subheader } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";

import util from "utils/utils";

import type { ActivityFeedPostWidgetProps } from "./types";

const WidgetContainer = styled.div`
  width: 100%;
  font-family: "Inter", sans-serif;
  box-shadow: 0.5px 0.5px 2.5px 1px #c0c0c0;
  border: none;
  border-radius: 5px;
  margin-bottom: 20px;
`;

const NewPostButton = styled(WidgetContainer)`
  background-color: ${({ theme }) => theme.primaryColour};
  color: ${({ theme }) => (theme.shouldBeWhiteOnPrimary ? "#fff" : "#000")};
  &:hover {
    background-color: ${({ theme }) =>
      util.adjustColour(theme.primaryColour, -0.1)};
  }
  padding: 1rem;
  display: flex;
  justify-content: center;
  align-items: center;
  span,
  i,
  .ui.icon {
    display: block;
    font-weight: 600;
  }
  i,
  .ui.icon {
    margin-right: 5px;
    transform: translateY(-3.5px);
  }
  > span {
  }
  cursor: pointer;
`;

const PostEditor = styled(WidgetContainer)`
  padding: 1rem;
  background-color: white;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;

  > span {
    color: gray;
    margin-bottom: 1rem;
  }

  .actions {
    display: flex;
    width: 100%;
    justify-content: flex-end;
  }
`;

const ActivityFeedPostWidget = ({
  onPost,
  isGroupFeed,
}: ActivityFeedPostWidgetProps) => {
  const [showEditor, setShowEditor] = useState(false);
  const [message, setMessage] = useState("");
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [isPinned, setIsPinned] = useState(false);

  const handlePost = useCallback(() => {
    onPost({ message, isAnonymous, isPinned, content: message });
    setMessage("");
    setIsAnonymous(false);
    setIsPinned(false);
    setShowEditor(false);
  }, [message, isAnonymous, isPinned, onPost]);

  if (!showEditor) {
    return (
      <NewPostButton onClick={() => setShowEditor(true)}>
        <Icon icon={PlusIcon} />
        <span>Create new feed post</span>
      </NewPostButton>
    );
  }
  return (
    <PostEditor>
      <div style={{ width: "100%" }}>
        <H5>
          New activity feed post
          <Subheader>
            These posts will show to{" "}
            {isGroupFeed
              ? "users in this group in the activity feed"
              : "users of this organisation in their activity feed"}
            . By default, they will appear at the top of the feeds for 7 days,
            after which they will filter into the timeline.
          </Subheader>
        </H5>
        <RichText
          preset="simple"
          value={message}
          onChange={(value) => setMessage(value)}
        />
      </div>

      <div>
        <Checkbox
          toggle
          checked={isPinned}
          onChange={() => setIsPinned(!isPinned)}
        >
          {`Toggle this on to pin the post to the top of ${isGroupFeed ? "the group activity feed" : "all users' activity feeds"}. If multiple posts are pinned, they will be ordered by recency.`}
        </Checkbox>
        <Checkbox
          toggle
          checked={isAnonymous}
          onChange={() => setIsAnonymous(!isAnonymous)}
        >
          {`Toggle this on to post anonymously. The ${isGroupFeed ? "group" : "organisation"} name will be shown as the author.`}
        </Checkbox>
      </div>
      <div className="actions">
        <Button onClick={handlePost} color="primary">
          <Icon icon={SendIcon} />
          Publish post
        </Button>
      </div>
    </PostEditor>
  );
};

export default ActivityFeedPostWidget;
